/**
 * Algolia Star Rating Filter Widget
 * Provides visual star-based filtering for product ratings
 */

(function() {
  'use strict';

  // Theme-level safety guard: do nothing when rating filter is disabled.
  if (
    window.themeSettings &&
    Object.prototype.hasOwnProperty.call(window.themeSettings, 'enableRatingFilter') &&
    !window.themeSettings.enableRatingFilter
  ) {
    return;
  }

  // Configuration
  var RATING_LEVELS = [
    { stars: 5, label: '5 Stars', min: 5, max: 5 },
    { stars: 4, label: '4 Stars & Up', min: 4, max: 5 },
    { stars: 3, label: '3 Stars & Up', min: 3, max: 5 },
    { stars: 2, label: '2 Stars & Up', min: 2, max: 5 },
    { stars: 1, label: '1 Star & Up', min: 1, max: 5 }
  ];

  var currentFilter = null;
  var searchInstance = null;
  var _docListenersBound = false;

  /**
   * Creates star display HTML
   * @param {number} rating - Number of stars to display as filled
   * @returns {string} HTML string for star display
   */
  function createStarDisplay(rating) {
    var stars = '';
    for (var i = 1; i <= 5; i++) {
      if (i <= rating) {
        stars += '<span class="star star-filled" aria-hidden="true">★</span>';
      } else {
        stars += '<span class="star star-empty" aria-hidden="true">☆</span>';
      }
    }
    return '<span class="algolia-star-display" aria-label="' + rating + ' stars and up">' + stars + '</span>';
  }

  /**
   * Updates URL with rating filter
   * @param {number|null} minRating - Minimum rating to add to URL
   */
  function updateURLWithRating(minRating) {
    try {
      var url = new URL(window.location);
      var params = url.searchParams;
      params.delete('numericFilters');
      if (minRating !== null && minRating > 0) {
        params.set('numericFilters', 'meta.custom.ratings>=' + minRating);
      }
      window.history.replaceState({}, '', url.toString());
    } catch (e) {}
  }

  /**
   * Applies rating filter to search
   * @param {number|null} minRating - Minimum rating to filter by (null to clear)
   */
  function applyRatingFilter(minRating) {
    if (!searchInstance) return;

    var helper = null;
    if (searchInstance.search) {
      helper = searchInstance.search.helper ||
               searchInstance.search.mainHelper ||
               searchInstance.search._helper;
    }
    if (!helper) {
      helper = searchInstance.helper || searchInstance.mainHelper || searchInstance._helper;
    }
    if (!helper && window.algoliaShopify && window.algoliaShopify.instantsearch) {
      helper = window.algoliaShopify.instantsearch.helper ||
               window.algoliaShopify.instantsearch.mainHelper ||
               window.algoliaShopify.instantsearch._helper;
    }
    if (!helper) return;

    helper.clearRefinements('rating');
    helper.clearRefinements('meta.custom.ratings');

    if (minRating !== null && minRating > 0) {
      var flooredRating = Math.floor(minRating);
      helper.addNumericRefinement('meta.custom.ratings', '>=', flooredRating);
      currentFilter = flooredRating;
      updateURLWithRating(flooredRating);
      if (typeof trackRatingFilter === 'function') {
        trackRatingFilter(flooredRating);
      }
    } else {
      currentFilter = null;
      updateURLWithRating(null);
    }

    helper.search();
  }

  /**
   * Updates rating counts in the UI
   * @param {object} results - Algolia search results
   */
  function updateRatingCounts(results) {
    if (!results || !results.facets || !results.facets['meta.custom.ratings']) return;

    var ratingFacet = results.facets['meta.custom.ratings'];
    var buckets = {};

    // Initialize buckets
    RATING_LEVELS.forEach(function(level) {
      buckets[level.stars] = 0;
    });

    // Count from facets
    Object.keys(ratingFacet).forEach(function(rating) {
      var ratingValue = parseFloat(rating);
      var count = ratingFacet[rating];
      
      RATING_LEVELS.forEach(function(level) {
        if (ratingValue >= level.min) {
          buckets[level.stars] += count;
        }
      });
    });

    // Update UI counts
    RATING_LEVELS.forEach(function(level) {
      var countElement = document.querySelector(
        '.algolia-star-rating-button[data-rating="' + level.stars + '"] .algolia-star-rating-count'
      );
      if (countElement) {
        var count = buckets[level.stars];
        countElement.textContent = '(' + count + ')';
        countElement.style.display = count > 0 ? 'inline' : 'none';
      }
    });
  }

  /**
   * Creates the rating filter UI
   * @param {string} containerId - ID of container element
   */
  function createRatingFilterUI(containerId) {
    var container = document.getElementById(containerId);
    if (!container) return;

    // Clear existing content
    container.innerHTML = '';

    var list = document.createElement('ul');
    list.className = 'algolia-star-rating-list';
    list.setAttribute('role', 'group');
    list.setAttribute('aria-label', 'Filter by customer rating');

    // Create rating filter buttons
    RATING_LEVELS.forEach(function(level) {
      var item = document.createElement('li');
      item.className = 'algolia-star-rating-item';

      var button = document.createElement('button');
      button.className = 'algolia-star-rating-button';
      button.type = 'button';
      button.setAttribute('data-rating', level.stars);
      button.setAttribute('data-min', level.min);
      button.setAttribute('aria-label', 'Filter by ' + level.label);

      var starsHtml = createStarDisplay(level.stars);
      var textSpan = '<span class="algolia-star-rating-text">' + level.label + '</span>';
      var countSpan = '<span class="algolia-star-rating-count"></span>';

      button.innerHTML = starsHtml + textSpan + countSpan;

      // Add click handler
      button.addEventListener('click', function() {
        var allButtons = list.querySelectorAll('.algolia-star-rating-button');
        allButtons.forEach(function(btn) {
          btn.classList.remove('active');
          btn.setAttribute('aria-pressed', 'false');
        });

        button.classList.add('active');
        button.setAttribute('aria-pressed', 'true');

        applyRatingFilter(level.min);
      });

      item.appendChild(button);
      list.appendChild(item);
    });

    container.appendChild(list);
  }

  /**
   * Reads rating filter from URL or storage
   * @returns {number|null} Rating value
   */
  function getSavedRating() {
    // Try URL first
    var params = new URLSearchParams(window.location.search);
    var numericFilters = params.get('numericFilters');
    if (numericFilters && numericFilters.indexOf('meta.custom.ratings>=') !== -1) {
      var match = numericFilters.match(/meta\.custom\.ratings>=(\d+)/);
      if (match) {
        return parseInt(match[1], 10);
      }
    }
    
    return null;
  }

  /**
   * Syncs UI state with current filter
   */
  function syncUIState() {
    var container = document.getElementById('rating-filter-container');
    if (!container) return;
    
    var allButtons = container.querySelectorAll('.algolia-star-rating-button');
    
    if (currentFilter) {
      // A rating filter is active
      allButtons.forEach(function(btn) {
        var rating = parseInt(btn.getAttribute('data-rating'), 10);
        if (rating === currentFilter) {
          btn.classList.add('active');
          btn.setAttribute('aria-pressed', 'true');
        } else {
          btn.classList.remove('active');
          btn.setAttribute('aria-pressed', 'false');
        }
      });
    } else {
      // No rating filter
      allButtons.forEach(function(btn) {
        btn.classList.remove('active');
        btn.setAttribute('aria-pressed', 'false');
      });
    }
  }

  /**
   * Initializes the rating filter
   * @param {object} search - InstantSearch instance
   * @param {string} containerId - ID of container element
   */
  function initRatingFilter(search, containerId) {
    if (!search) return;

    var isNewSearchInstance = search !== searchInstance;
    searchInstance = search;
    createRatingFilterUI(containerId || 'rating-filter-container');

    // Check for saved rating filter from URL
    var savedRating = getSavedRating();
    if (savedRating) {
      setTimeout(function() {
        var button = document.querySelector('.algolia-star-rating-button[data-rating="' + savedRating + '"]');
        if (button) {
          button.click();
        }
      }, 500);
    }

    // Update counts on render
    if (isNewSearchInstance && search.on) {
      search.on('render', function() {
        var helper = (search.search && (search.search.helper || search.search.mainHelper)) ||
                     search.helper || search.mainHelper;
        var results = helper ? helper.lastResults : null;
        if (results) {
          updateRatingCounts(results);
        }
        if (helper && helper.state && helper.state.numericRefinements &&
            helper.state.numericRefinements['meta.custom.ratings'] &&
            helper.state.numericRefinements['meta.custom.ratings']['>='] &&
            helper.state.numericRefinements['meta.custom.ratings']['>='].length) {
          currentFilter = helper.state.numericRefinements['meta.custom.ratings']['>='][0];
        } else {
          // Handle external clear actions (e.g. Algolia "Clear all")
          // by keeping the URL in sync when rating refinement is removed.
          updateURLWithRating(null);
          currentFilter = null;
        }
        syncUIState();
      });
    }

    if (_docListenersBound) return;
    _docListenersBound = true;

    // Fallback: some themes handle clear actions outside this render cycle.
    // Listen directly and force rating URL cleanup/UI sync.
    document.addEventListener('click', function(evt) {
      var target = evt.target;
      if (!target || !target.closest) return;

      var clearBtn = target.closest('.ais-ClearRefinements-button, .ais-clear-all, [class*="clear-refinements"], [data-action="clear-refinements"]');
      var clearRatingBtn = target.closest('.ais-CurrentRefinements-delete, .ais-current-refinements--clear, [class*="current-refinements"] button, .ais-current-refined-values--link');

      // Handle global clear-all
      if (clearBtn) {
        // Let Algolia finish its clear action first.
        setTimeout(function() {
          currentFilter = null;
          updateURLWithRating(null);
          syncUIState();
        }, 80);
        return;
      }

      // Handle single-refinement clear for rating
      if (clearRatingBtn) {
        var refinementNode = clearRatingBtn.closest('.ais-CurrentRefinements-item, .ais-current-refinements--item') || clearRatingBtn.parentElement;
        var refinementText = (refinementNode && refinementNode.textContent ? refinementNode.textContent : '').toLowerCase();
        var ariaLabel = ((clearRatingBtn.getAttribute('aria-label') || '') + ' ' + (clearRatingBtn.getAttribute('title') || '')).toLowerCase();
        var dataAttribute = (clearRatingBtn.getAttribute('data-attribute') || '').toLowerCase();
        var dataType = (clearRatingBtn.getAttribute('data-type') || '').toLowerCase();
        var isRatingRefinement =
          refinementText.indexOf('rating') !== -1 ||
          refinementText.indexOf('meta.custom.ratings') !== -1 ||
          ariaLabel.indexOf('rating') !== -1 ||
          ariaLabel.indexOf('meta.custom.ratings') !== -1 ||
          dataAttribute === 'meta.custom.ratings' ||
          (dataType === 'numeric' && dataAttribute.indexOf('ratings') !== -1);

        if (!isRatingRefinement) return;

        setTimeout(function() {
          currentFilter = null;
          updateURLWithRating(null);
          syncUIState();
        }, 80);
      }
    }, true);

    // Additional robust fallback:
    // any click inside current refinements that appears rating-related.
    document.addEventListener('click', function(evt) {
      var target = evt.target;
      if (!target || !target.closest) return;

      var inCurrentRefinements = target.closest('.ais-CurrentRefinements, .ais-current-refinements, .ais-current-refined-values, [class*="current-refinements"], [class*="current-refined-values"]');
      if (!inCurrentRefinements) return;

      var nodeText = (target.textContent || '').toLowerCase();
      var parentItem = target.closest('.ais-CurrentRefinements-item, .ais-current-refinements--item, li, button, a') || target;
      var parentText = (parentItem.textContent || '').toLowerCase();
      var combined = (nodeText + ' ' + parentText).toLowerCase();

      // Ignore if it's clearly not rating-related.
      if (combined.indexOf('rating') === -1 && combined.indexOf('meta.custom.ratings') === -1) return;

      setTimeout(function() {
        // If rating refinement is gone from state, sync URL/UI.
        var s = searchInstance || {};
        var helperNow = (s.search && (s.search.helper || s.search.mainHelper)) ||
                        s.helper || s.mainHelper;
        var hasRating = !!(
          helperNow &&
          helperNow.state &&
          helperNow.state.numericRefinements &&
          helperNow.state.numericRefinements['meta.custom.ratings'] &&
          helperNow.state.numericRefinements['meta.custom.ratings']['>='] &&
          helperNow.state.numericRefinements['meta.custom.ratings']['>='].length
        );

        if (!hasRating) {
          currentFilter = null;
          updateURLWithRating(null);
          syncUIState();
        }
      }, 120);
    }, true);
  }

  /**
   * Injects rating filter into Algolia sidebar
   */
  function injectRatingFilter() {
    // Check if already injected
    if (document.getElementById('algolia-star-rating-filter')) return;
    
    // Find the default ratings filter to get its label and position
    var defaultRatingLabel = document.querySelector('label[for="meta.custom.ratings"]');
    var defaultRatingWrapper = defaultRatingLabel ? defaultRatingLabel.closest('.ais-facet-dropdown-wrapper') : null;
    
    var labelText = 'Ratings'; // Default
    var insertPosition = null;
    var filterSidebar = null;
    
    if (defaultRatingLabel) {
      labelText = defaultRatingLabel.textContent.trim();
    }
    
    if (defaultRatingWrapper) {
      insertPosition = defaultRatingWrapper;
      filterSidebar = defaultRatingWrapper.parentElement;
    } else {
      // Fallback: find the sidebar
      var selectors = ['.ais-facets', '.collection-facets-wrapper', '[class*="ais-facets"]'];
      for (var i = 0; i < selectors.length; i++) {
        filterSidebar = document.querySelector(selectors[i]);
        if (filterSidebar) break;
      }
    }
    
    if (!filterSidebar) return;

    var filterHTML = document.createElement('div');
    filterHTML.id = 'algolia-star-rating-filter';
    filterHTML.className = 'algolia-star-filter ais-facet-dropdown-wrapper ais-facet-conjunctive';
    filterHTML.innerHTML = '<label for="algolia-rating-dropdown" class="ais-range-slider--header ais-facet--header ais-header">' + labelText + '</label><input class="ais-dropdown-checkbox" type="checkbox" id="algolia-rating-dropdown" name="dropdown" checked><div class="ais-facet-dropdown-container"><div id="rating-filter-container"></div></div>';

    // Insert right after the default rating filter, or at the beginning
    if (insertPosition && insertPosition.nextSibling) {
      filterSidebar.insertBefore(filterHTML, insertPosition.nextSibling);
    } else if (insertPosition) {
      filterSidebar.appendChild(filterHTML);
    } else {
      filterSidebar.insertBefore(filterHTML, filterSidebar.firstChild);
    }
    
    // Now hide the default rating filter
    hideDefaultRatingFacets();
    
    // Initialize the UI immediately
    createRatingFilterUI('rating-filter-container');
  }
  
  /**
   * Hide default Algolia rating facet displays
   */
  function hideDefaultRatingFacets() {
    // Target the specific rating facet wrapper structure
    var ratingWrappers = [
      '.ais-facet-dropdown-wrapper.ais-facet-meta\\.custom\\.ratings',
      'div[class*="ais-facet-meta.custom.ratings"]',
      'label[for="meta.custom.ratings"]'
    ];
    
    ratingWrappers.forEach(function(selector) {
      var elements = document.querySelectorAll(selector);
      elements.forEach(function(el) {
        var wrapper = el.tagName === 'LABEL' ? el.closest('.ais-facet-dropdown-wrapper') : el;
        // Don't hide our custom filter
        if (wrapper && wrapper.id !== 'algolia-star-rating-filter') {
          wrapper.style.setProperty('display', 'none', 'important');
          wrapper.style.setProperty('visibility', 'hidden', 'important');
          wrapper.setAttribute('hidden', 'true');
        }
      });
    });
    
    // Target by label text (but skip our custom filter)
    var labels = document.querySelectorAll('label.ais-header, label.ais-facet--header');
    labels.forEach(function(label) {
      // Skip if it's our custom filter's label
      if (label.getAttribute('for') === 'algolia-rating-dropdown') {
        return;
      }
      
      var labelText = (label.textContent || '').toLowerCase();
      if (labelText.includes('rating')) {
        var wrapper = label.closest('.ais-facet-dropdown-wrapper');
        if (wrapper && wrapper.id !== 'algolia-star-rating-filter') {
          wrapper.style.setProperty('display', 'none', 'important');
          wrapper.style.setProperty('visibility', 'hidden', 'important');
          wrapper.setAttribute('hidden', 'true');
        }
      }
    });
  }

  /**
   * Analytics tracking for rating filters
   * @param {number} minRating - Minimum rating applied
   */
  function trackRatingFilter(minRating) {
    try {
      // Google Analytics
      if (window.gtag) {
        window.gtag('event', 'filter_rating', {
          'event_category': 'Search',
          'event_label': minRating + ' stars and up',
          'value': minRating
        });
      }

      // Klaviyo
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['track', 'Applied Rating Filter', {
          min_rating: minRating,
          page: window.location.pathname
        }]);
      }

      // Custom event
      document.dispatchEvent(new CustomEvent('algolia:rating-filter', {
        detail: { minRating: minRating },
        bubbles: true
      }));
    } catch (e) {
      console.error('[Rating Filter] Analytics error:', e);
    }
  }

  /**
   * Get current active filter
   * @returns {number|null} Current minimum rating filter
   */
  function getCurrentFilter() {
    return currentFilter;
  }

  /**
   * Reset rating filter
   */
  function resetFilter() {
    applyRatingFilter(null);
  }

  // Expose public API
  window.AlgoliaRatingFilter = {
    init: initRatingFilter,
    inject: injectRatingFilter,
    applyFilter: applyRatingFilter,
    getCurrentFilter: getCurrentFilter,
    reset: resetFilter,
    updateCounts: updateRatingCounts
  };

  // Auto-initialize - try multiple times
  function tryInit() {
    // Try to inject immediately
    setTimeout(function() {
      injectRatingFilter();
      
      if (window.algoliaShopify && window.algoliaShopify.instantsearch) {
        initRatingFilter(window.algoliaShopify.instantsearch, 'rating-filter-container');
      }
    }, 1000);
    
    // Try again after 2 seconds
    setTimeout(function() {
      injectRatingFilter();
      
      if (window.algoliaShopify && window.algoliaShopify.instantsearch) {
        initRatingFilter(window.algoliaShopify.instantsearch, 'rating-filter-container');
      }
    }, 2000);
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInit);
  } else {
    tryInit();
  }
  
  startRatingFacetWatcher();
  
  /**
   * Watch for default rating facets and hide them
   */
  function startRatingFacetWatcher() {
    var observer = new MutationObserver(function() {
      hideDefaultRatingFacets();
    });
    
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
    
    // Run periodically for first 10 seconds
    var checkCount = 0;
    var checkInterval = setInterval(function() {
      hideDefaultRatingFacets();
      checkCount++;
      if (checkCount > 20) {
        clearInterval(checkInterval);
      }
    }, 500);
  }

})();
